import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import './EtcRollPanel.css';

function fmtPct(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(2)}%`;
}

function fmtNum(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function structureClass(structure) {
  if (structure === 'contango') return 'etc-roll-contango';
  if (structure === 'backwardation') return 'etc-roll-backwardation';
  return '';
}

function hasRollDashboardData(data) {
  if (!data) return false;
  const structure = data.structure;
  if (structure === 'contango' || structure === 'backwardation') return true;
  if (data.roll_per_period_pct != null || data.roll_annualised_pct != null) return true;
  if (Array.isArray(data.curve) && data.curve.length > 0) return true;
  if (structure === 'n/a' && data.note) return true;
  return false;
}

function curveToSeries(curve) {
  if (!curve?.length) return [];
  const base = new Date('2026-01-01T00:00:00Z');
  return curve.map((pt, i) => {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + i);
    return { time: d.toISOString().slice(0, 10), value: pt.price };
  });
}

function EtcRollPanel({
  symbol,
  replication,
  category,
  onLayoutChange,
  className = '',
  hideIfUnavailable = false,
}) {
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const isFuturesLinked = category === 'etc' || category === 'etn';
  const isFutures = replication === 'futures';
  const showSection = isFuturesLinked && isFutures;

  useEffect(() => {
    if (!symbol || !showSection) {
      setData(null);
      setLoadFailed(false);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadFailed(false);
      try {
        const res = await fetch(`/api/investing/etc/roll-yield/${encodeURIComponent(symbol)}`);
        if (!res.ok) {
          if (!cancelled) {
            setData(null);
            setLoadFailed(true);
          }
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) {
          setData(null);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, showSection]);

  useEffect(() => {
    onLayoutChange?.();
  }, [loading, data?.obs_date, data?.structure, data?.curve?.length, onLayoutChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !data?.curve?.length) return undefined;

    const chart = createChart(el, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', visible: false },
      width: el.clientWidth,
      height: 140,
    });

    const series = chart.addSeries(LineSeries, {
      color: '#A16917',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const points = curveToSeries(data.curve);
    if (points.length) {
      series.setData(points);
      chart.timeScale().fitContent();
    }

    const disconnect = observeChartSize(el, (width) => {
      chart.applyOptions({ width, height: 140 });
    });

    return () => {
      disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [symbol, data?.obs_date, data?.curve?.length]);

  if (!showSection) return null;

  const panelClass = `etp-term-structure-dashboard etc-roll-panel ${className}`.trim();

  if (loading) {
    return (
      <section className={panelClass} aria-label="Futures term structure">
        <h4 className="etc-roll-title">Term structure &amp; roll yield</h4>
        <p className="etc-roll-muted">Loading curve data…</p>
      </section>
    );
  }

  if (loadFailed) {
    if (hideIfUnavailable) return null;
    return (
      <section className={panelClass} aria-label="Futures term structure">
        <h4 className="etc-roll-title">Term structure &amp; roll yield</h4>
        <p className="etc-roll-muted">
          No roll-yield snapshot for {symbol} yet. Run{' '}
          <code className="etc-roll-inline-code">python -m investing.backend --etc-roll-only</code>
          {' '}after restarting the investing backend.
        </p>
      </section>
    );
  }

  if (!hasRollDashboardData(data)) return null;

  const showMetrics = data.structure === 'contango' || data.structure === 'backwardation';

  return (
    <section className={panelClass} aria-label="Futures term structure">
      <h4 className="etc-roll-title">Term structure &amp; roll yield</h4>
      {showMetrics && (
      <div className="etc-roll-summary">
        <span className={`etc-roll-structure ${structureClass(data.structure)}`}>
          {(data.structure || '—').replace(/_/g, ' ')}
        </span>
        <span className="etc-roll-meta">
          as of {data.obs_date}
          {data.futures_root ? ` · ${data.futures_root}` : ''}
        </span>
      </div>
      )}
      {showMetrics && (
      <table className="etc-roll-table">
        <tbody>
          <tr>
            <th>Roll (per period)</th>
            <td className="investing-mono">{fmtPct(data.roll_per_period_pct)}</td>
            <th>Roll (annualised)</th>
            <td className="investing-mono">{fmtPct(data.roll_annualised_pct)}</td>
          </tr>
          <tr>
            <th>Front</th>
            <td className="investing-mono" colSpan={3}>
              {data.front_contract || '—'}
              {' '}
              @ {fmtNum(data.front_price)}
            </td>
          </tr>
          <tr>
            <th>Next</th>
            <td className="investing-mono" colSpan={3}>
              {data.next_contract || '—'}
              {' '}
              @ {fmtNum(data.next_price)}
            </td>
          </tr>
        </tbody>
      </table>
      )}
      {data.note && <p className="etc-roll-note">{data.note}</p>}
      {data.curve?.length > 0 && (
        <>
          <p className="etc-roll-curve-label">Term structure (M1 → M{data.curve.length})</p>
          <div ref={containerRef} className="etc-roll-chart" />
          <table className="etc-roll-curve-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Contract</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {data.curve.map((row) => (
                <tr key={row.rank}>
                  <td>M{row.rank}</td>
                  <td>{row.contract}</td>
                  <td className="investing-mono">{fmtNum(row.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

export default EtcRollPanel;
